import * as THREE from "three"
import type { SceneSkyMarkup } from "@/lib/location-scene-3d/scene-state"

/** Starting-scene look. Distances in the reference are for a 10-wide block; scale by span / 10. */
export type SceneThemeId = "light" | "dark"

export type SceneTheme = {
  id: SceneThemeId
  skyTop: string
  skyMid: string
  skyBottom: string
  blockTop: string
  blockSide: string
  blockGrid: string
  marks: string
  fog: string
  hemiSky: string
  hemiGround: string
  hemiIntensity: number
  sunColor: string
  sunIntensity: number
  /** Видимый шар лампы. День — тёплый, ночь — почти белый. */
  lampColor: string
  lampGlow: string
}

const SCENE_INTENSITY: Record<SceneThemeId, { hemi: number; sun: number }> = {
  light: { hemi: 0.18, sun: 0.24 },
  dark: { hemi: 0.6, sun: 0.7 },
}

type SceneColorKey = Exclude<keyof SceneTheme, "id" | "hemiIntensity" | "sunIntensity">

const SCENE_COLOR_TOKENS: Record<SceneThemeId, Record<SceneColorKey, string>> = {
  light: {
    skyTop: "--scene-day-sky-top",
    skyMid: "--scene-day-sky-mid",
    skyBottom: "--scene-day-sky-bottom",
    blockTop: "--scene-day-block-top",
    blockSide: "--scene-day-block-side",
    blockGrid: "--scene-day-block-grid",
    marks: "--scene-day-marks",
    fog: "--scene-day-fog",
    hemiSky: "--scene-day-hemi-sky",
    hemiGround: "--scene-day-hemi-ground",
    sunColor: "--scene-sun",
    lampColor: "--scene-day-lamp",
    lampGlow: "--scene-day-lamp-glow",
  },
  dark: {
    skyTop: "--scene-night-sky-top",
    skyMid: "--scene-night-sky-mid",
    skyBottom: "--scene-night-sky-bottom",
    blockTop: "--scene-night-block-top",
    blockSide: "--scene-night-block-side",
    blockGrid: "--scene-night-block-grid",
    marks: "--scene-night-marks",
    fog: "--scene-night-fog",
    hemiSky: "--scene-night-hemi-sky",
    hemiGround: "--scene-night-hemi-ground",
    sunColor: "--scene-sun",
    lampColor: "--scene-night-lamp",
    lampGlow: "--scene-night-lamp-glow",
  },
}

function cssColorToHex(color: string): string {
  const match = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/)
  if (!match) return "#000000"
  const channel = (value: string) =>
    Math.max(0, Math.min(255, Math.round(Number(value))))
      .toString(16)
      .padStart(2, "0")
  return `#${channel(match[1])}${channel(match[2])}${channel(match[3])}`
}

function readTokenHex(token: string): string {
  const probe = document.createElement("span")
  probe.style.backgroundColor = `var(${token})`
  document.documentElement.appendChild(probe)
  const color = getComputedStyle(probe).backgroundColor
  probe.remove()
  return cssColorToHex(color)
}

export function sceneThemeById(id: SceneThemeId | null | undefined): SceneTheme {
  const themeId: SceneThemeId = id === "dark" ? "dark" : "light"
  const tokens = SCENE_COLOR_TOKENS[themeId]
  const intensity = SCENE_INTENSITY[themeId]
  return {
    id: themeId,
    skyTop: readTokenHex(tokens.skyTop),
    skyMid: readTokenHex(tokens.skyMid),
    skyBottom: readTokenHex(tokens.skyBottom),
    blockTop: readTokenHex(tokens.blockTop),
    blockSide: readTokenHex(tokens.blockSide),
    blockGrid: readTokenHex(tokens.blockGrid),
    marks: readTokenHex(tokens.marks),
    fog: readTokenHex(tokens.fog),
    hemiSky: readTokenHex(tokens.hemiSky),
    hemiGround: readTokenHex(tokens.hemiGround),
    hemiIntensity: intensity.hemi,
    sunColor: readTokenHex(tokens.sunColor),
    sunIntensity: intensity.sun,
    lampColor: readTokenHex(tokens.lampColor),
    lampGlow: readTokenHex(tokens.lampGlow),
  }
}

/** Reference block is 10 wide. Editor span is meters across the platform. */
export const THEME_REFERENCE_SPAN = 10

export function themeScaleForSpan(span: number): number {
  const safe = Number.isFinite(span) && span > 0 ? span : 32
  return safe / THEME_REFERENCE_SPAN
}

/** Starting camera looks down at −33° with a 55° vertical FOV, so the frame spans about −61°…−6°. */
export const SKY_ELEV = { top: -6, mid: -36, bottom: -61 } as const

/** Half of the previous cross arms (0.9 / 1.8), still scaled by span / 10. */
export const MARK_MINOR_ARM = 0.45
export const MARK_MAJOR_ARM = 0.9

export const GRID_MAJOR_EVERY = 4
export const GRID_MINOR_WIDTH_OF_CELL = 0.025

const SKY_VERTEX = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

const SKY_FRAGMENT = /* glsl */ `
varying vec3 vDir;
uniform vec3 uTop;
uniform vec3 uMid;
uniform vec3 uBottom;
uniform float uElevTop;
uniform float uElevMid;
uniform float uElevBottom;
void main() {
  float elev = degrees(asin(clamp(normalize(vDir).y, -1.0, 1.0)));
  vec3 col = elev >= uElevMid
    ? mix(uMid, uTop, clamp((elev - uElevMid) / (uElevTop - uElevMid), 0.0, 1.0))
    : mix(uBottom, uMid, clamp((elev - uElevBottom) / (uElevMid - uElevBottom), 0.0, 1.0));
  gl_FragColor = vec4(col, 1.0);
  #include <colorspace_fragment>
}
`

/** Crosses on a sphere. Majors when both azimuth and elevation indices are multiples of 3. */
export function buildSkyMarkPositions(
  radius: number,
  minorArm: number,
  majorArm: number,
): { major: number[]; minor: number[]; majorPoints: number[]; minorPoints: number[] } {
  const major: number[] = []
  const minor: number[] = []
  const majorPoints: number[] = []
  const minorPoints: number[] = []
  const pushCross = (
    dest: number[],
    px: number,
    py: number,
    pz: number,
    tx: number,
    ty: number,
    tz: number,
    bx: number,
    by: number,
    bz: number,
    arm: number,
  ) => {
    dest.push(
      px - tx * arm,
      py - ty * arm,
      pz - tz * arm,
      px + tx * arm,
      py + ty * arm,
      pz + tz * arm,
      px - bx * arm,
      py - by * arm,
      pz - bz * arm,
      px + bx * arm,
      py + by * arm,
      pz + bz * arm,
    )
  }

  for (let az = 0; az < 360; az += 15) {
    const ai = az / 15
    for (let el = -75; el <= 75; el += 15) {
      const ei = el / 15
      const isMajor = ai % 3 === 0 && ei % 3 === 0
      const cosEl = Math.cos((el * Math.PI) / 180)
      if (!isMajor && cosEl < 0.5 && ai % 2 === 1) continue
      const phi = ((90 - el) * Math.PI) / 180
      const theta = (az * Math.PI) / 180
      const sinPhi = Math.sin(phi)
      const px = radius * sinPhi * Math.sin(theta)
      const py = radius * Math.cos(phi)
      const pz = radius * sinPhi * Math.cos(theta)
      const len = Math.hypot(px, py, pz) || 1
      const nx = px / len
      const nz = pz / len
      let tx = nz
      let ty = 0
      let tz = -nx
      const tLen = Math.hypot(tx, ty, tz)
      if (tLen < 1e-8) continue
      tx /= tLen
      ty /= tLen
      tz /= tLen
      const ny = py / len
      const bx = ny * tz - nz * ty
      const by = nz * tx - nx * tz
      const bz = nx * ty - ny * tx
      ;(isMajor ? majorPoints : minorPoints).push(px, py, pz)
      pushCross(isMajor ? major : minor, px, py, pz, tx, ty, tz, bx, by, bz, isMajor ? majorArm : minorArm)
    }
  }
  return { major, minor, majorPoints, minorPoints }
}

function lineGeometry(positions: number[]): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry()
  const data = positions.length > 0 ? positions : [0, 0, 0, 0, 0, 0]
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(data, 3))
  return geometry
}

export type SceneThemeRig = {
  apply: (
    theme: SceneTheme,
    span: number,
    lights: { hemi: THREE.HemisphereLight; key: THREE.DirectionalLight; fill: THREE.DirectionalLight },
  ) => void
  setMarkup: (markup: SceneSkyMarkup) => void
  syncToCamera: (camera: THREE.Camera) => void
  dispose: () => void
}

function dotCircleTexture(): THREE.CanvasTexture {
  const size = 64
  const canvas = document.createElement("canvas")
  canvas.width = size
  canvas.height = size
  const g = canvas.getContext("2d")
  if (g) {
    g.clearRect(0, 0, size, size)
    g.beginPath()
    g.arc(size / 2, size / 2, size / 2 - 0.5, 0, Math.PI * 2)
    g.fillStyle = "#ffffff"
    g.fill()
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

function pointMaterial(opacity: number, map: THREE.Texture, color: string) {
  return new THREE.PointsMaterial({
    color,
    map,
    size: 1,
    transparent: true,
    opacity,
    depthWrite: false,
    toneMapped: false,
    fog: false,
    sizeAttenuation: true,
  })
}

export function createSceneThemeRig(scene: THREE.Scene, camera: THREE.PerspectiveCamera): SceneThemeRig {
  const day = sceneThemeById("light")
  scene.background = null
  scene.fog = null

  const skyUniforms = {
    uTop: { value: new THREE.Color(day.skyTop) },
    uMid: { value: new THREE.Color(day.skyMid) },
    uBottom: { value: new THREE.Color(day.skyBottom) },
    uElevTop: { value: SKY_ELEV.top },
    uElevMid: { value: SKY_ELEV.mid },
    uElevBottom: { value: SKY_ELEV.bottom },
  }
  const sky = new THREE.Mesh(
    new THREE.SphereGeometry(1, 64, 32),
    new THREE.ShaderMaterial({
      uniforms: skyUniforms,
      vertexShader: SKY_VERTEX,
      fragmentShader: SKY_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  )
  sky.name = "sceneThemeSky"
  sky.renderOrder = -1
  sky.frustumCulled = false
  sky.raycast = () => {}
  sky.userData = { kind: "sceneThemeSky", ignorePick: true }
  scene.add(sky)

  const markMaterial = (opacity: number) =>
    new THREE.LineBasicMaterial({
      color: day.marks,
      transparent: true,
      opacity,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    })
  const major = new THREE.LineSegments(lineGeometry([]), markMaterial(0.9))
  const minor = new THREE.LineSegments(lineGeometry([]), markMaterial(0.45))
  const dotMap = dotCircleTexture()
  const majorDots = new THREE.Points(lineGeometry([]), pointMaterial(0.9, dotMap, day.marks))
  const minorDots = new THREE.Points(lineGeometry([]), pointMaterial(0.45, dotMap, day.marks))
  for (const marks of [major, minor, majorDots, minorDots]) {
    marks.frustumCulled = false
    marks.raycast = () => {}
    marks.userData = { kind: "sceneThemeMarks", ignorePick: true }
    marks.visible = false
    scene.add(marks)
  }

  const sun = new THREE.DirectionalLight(day.sunColor, day.sunIntensity)
  sun.name = "sceneThemeSun"
  sun.castShadow = false
  scene.add(sun)
  scene.add(sun.target)

  const apply = (
    theme: SceneTheme,
    span: number,
    lights: { hemi: THREE.HemisphereLight; key: THREE.DirectionalLight; fill: THREE.DirectionalLight },
  ) => {
    skyUniforms.uTop.value.set(theme.skyTop)
    skyUniforms.uMid.value.set(theme.skyMid)
    skyUniforms.uBottom.value.set(theme.skyBottom)
    const k = themeScaleForSpan(span)
    const markRadius = 120 * k
    const skyRadius = 1000 * k
    sky.scale.setScalar(skyRadius)
    const nextFar = Math.max(400, skyRadius * 1.05)
    if (camera.far !== nextFar) {
      camera.far = nextFar
      camera.updateProjectionMatrix()
    }

    const minorArm = MARK_MINOR_ARM * k
    const majorArm = MARK_MAJOR_ARM * k
    const marks = buildSkyMarkPositions(markRadius, minorArm, majorArm)
    major.geometry.dispose()
    minor.geometry.dispose()
    majorDots.geometry.dispose()
    minorDots.geometry.dispose()
    major.geometry = lineGeometry(marks.major)
    minor.geometry = lineGeometry(marks.minor)
    majorDots.geometry = lineGeometry(marks.majorPoints)
    minorDots.geometry = lineGeometry(marks.minorPoints)
    ;(major.material as THREE.LineBasicMaterial).color.set(theme.marks)
    ;(minor.material as THREE.LineBasicMaterial).color.set(theme.marks)
    const majorDotMat = majorDots.material as THREE.PointsMaterial
    const minorDotMat = minorDots.material as THREE.PointsMaterial
    majorDotMat.color.set(theme.marks)
    minorDotMat.color.set(theme.marks)
    minorDotMat.size = minorArm
    majorDotMat.size = majorArm

    scene.fog = null
    lights.hemi.color.set(theme.hemiSky)
    lights.hemi.groundColor.set(theme.hemiGround)
    lights.hemi.intensity = theme.hemiIntensity
    lights.key.intensity = 0
    lights.fill.intensity = 0
    sun.color.set(theme.sunColor)
    sun.intensity = theme.sunIntensity
    sun.castShadow = false
    sun.position.set(-8 * k, 16 * k, 6 * k)
    sun.target.position.set(0, 0, 0)
    sun.target.updateMatrixWorld()
  }

  const setMarkup = (markup: SceneSkyMarkup) => {
    const plus = markup === "plus"
    const dots = markup === "dots"
    major.visible = plus
    minor.visible = plus
    majorDots.visible = dots
    minorDots.visible = dots
  }

  const syncToCamera = (next: THREE.Camera) => {
    sky.position.copy(next.position)
  }

  const dispose = () => {
    scene.background = null
    scene.fog = null
    scene.remove(sky)
    scene.remove(major)
    scene.remove(minor)
    scene.remove(majorDots)
    scene.remove(minorDots)
    scene.remove(sun)
    scene.remove(sun.target)
    sky.geometry.dispose()
    ;(sky.material as THREE.Material).dispose()
    for (const marks of [major, minor, majorDots, minorDots]) {
      marks.geometry.dispose()
      ;(marks.material as THREE.Material).dispose()
    }
    dotMap.dispose()
  }

  return { apply, setMarkup, syncToCamera, dispose }
}
